import React, { createContext, useContext, useState } from 'react'

export interface SellerQueueItem {
  id: number
  pluginspaceId: number
  sellerId: number
  sellerName?: string
  position: number
  status: 'available' | 'in_service' | 'offline' | 'paused'
  lastCalledAt: string | null
  lastFinishedAt: string | null
  created: string
  updated: string
  user?: {
    id: number
    name: string
    email: string
  }
}

export interface SellerServiceSessionItem {
  id: number
  pluginspaceId: number
  sellerId: number
  sellerName?: string
  queuePositionAtStart: number
  status: 'waiting' | 'in_progress' | 'finished'
  outcome: 'sale_completed' | 'pending_checkout' | 'no_sale' | 'abandoned' | null
  cartId: number | null
  orderId: number | null
  startedAt: string
  finishedAt: string | null
  durationSeconds: number | null
  createdBy: number | null
  updated: string
}

export interface SellerServiceSessionCustomerItem {
  id: number
  sessionId: number
  sequenceNumber: number
  customerId: number | null
  cartId: number | null
  orderId: number | null
  status: 'in_progress' | 'finished' | 'abandoned'
  outcome: 'sale_completed' | 'pending_checkout' | 'no_sale' | 'abandoned' | null
  saleAmount?: number | string | null
  startedAt: string
  finishedAt: string | null
}

export interface SellerServiceNoSaleReasonItem {
  id: number
  sessionId: number
  reasonCode: number
  productName: string | null
  sizeWanted: string | null
  colorWanted: string | null
  note: string | null
  created: string
  updated: string
}

export interface NoSaleReasonCodeOption {
  id: number
  slugSaleReasonCode: string
  saleReasonCode: string
  pluginspaceId: number
  created: string
  updated: string
  deleted: string | null
}

export interface SellerQueueContextType {
  // Queue State
  queueEntry: SellerQueueItem | null
  isLoadingQueue: boolean
  errorQueue: Error | null

  // Session State
  currentSession: SellerServiceSessionItem | null
  isLoadingSession: boolean
  errorSession: Error | null

  // Metadata
  lastUpdated: Date | null
  isOnline: boolean

  // Actions
  setQueueEntry: (entry: SellerQueueItem | null) => void
  setCurrentSession: (session: SellerServiceSessionItem | null) => void
  setIsLoadingQueue: (loading: boolean) => void
  setIsLoadingSession: (loading: boolean) => void
  setErrorQueue: (error: Error | null) => void
  setErrorSession: (error: Error | null) => void
  setLastUpdated: (date: Date | null) => void
  setIsOnline: (online: boolean) => void
}

const SellerQueueContext = createContext<SellerQueueContextType | undefined>(undefined)

export const SellerQueueProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [queueEntry, setQueueEntry] = useState<SellerQueueItem | null>(null)
  const [isLoadingQueue, setIsLoadingQueue] = useState(false)
  const [errorQueue, setErrorQueue] = useState<Error | null>(null)

  const [currentSession, setCurrentSession] = useState<SellerServiceSessionItem | null>(null)
  const [isLoadingSession, setIsLoadingSession] = useState(false)
  const [errorSession, setErrorSession] = useState<Error | null>(null)

  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [isOnline, setIsOnline] = useState(true)

  const value: SellerQueueContextType = {
    queueEntry,
    isLoadingQueue,
    errorQueue,
    currentSession,
    isLoadingSession,
    errorSession,
    lastUpdated,
    isOnline,
    setQueueEntry,
    setIsLoadingQueue,
    setErrorQueue,
    setCurrentSession,
    setIsLoadingSession,
    setErrorSession,
    setLastUpdated,
    setIsOnline,
  }

  return <SellerQueueContext.Provider value={value}>{children}</SellerQueueContext.Provider>
}

export const useSellerQueueContext = (): SellerQueueContextType => {
  const context = useContext(SellerQueueContext)
  if (!context) {
    throw new Error('useSellerQueueContext must be used within SellerQueueProvider')
  }
  return context
}
